import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckCircle2Icon, ShieldCheckIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Button } from '../components/ui/Button';
import { CtaBand } from '../components/ui/CtaBand';
const amounts = ['₦2,000', '₦5,000', '₦10,000', '₦25,000', '₦50,000', '₦100,000'];
const rules = ['Contributions are accepted only from eligible Nigerian citizens, in their own name.', 'The campaign does not accept anonymous, corporate, foreign or cash contributions.', 'Every contribution is receipted with a reference number and reported as required by the Electoral Act.', 'No contribution buys access, appointments, contracts or influence over policy.'];
type Step = 'amount' | 'details' | 'review' | 'done';
export function Support() {
  const [step, setStep] = useState<Step>('amount');
  const [amount, setAmount] = useState('₦5,000');
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('');
  const [eligible, setEligible] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState(false);
  const finalAmount = custom ? `₦${Number(custom).toLocaleString('en-NG')}` : amount;
  const submitDetails = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Enter a valid email address.';
    if (!state.trim()) next.state = 'Enter your state of residence.';
    if (!eligible) next.eligible = 'You must confirm your eligibility to contribute.';
    setErrors(next);
    if (Object.keys(next).length === 0) setStep('review');
  };
  const inputClass = (hasError: boolean) => `w-full rounded-lg border bg-white px-4 py-3 text-[15px] text-ink placeholder:text-slate-500/70 focus:outline-none ${hasError ? 'border-red-600' : 'border-sand-300 focus:border-green-600'}`;
  return <>
      <PageHero eyebrow="Support the campaign" title="Funded by Nigerians. Answerable to Nigerians." intro="Small contributions from many people are what keep this campaign independent of any single interest." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Support'
    }]} data-id="element-1376" data-mp="pkk1vftx8tsl" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Contribution" data-id="element-1377" data-mp="14dx18niem8dx">
        <div className="mx-auto grid max-w-content gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-10" data-id="element-1378" data-mp="18ccwtfsjwp2y">
          <div className="lg:col-span-5" data-id="element-1379" data-mp="ulhk1sexlnu3">
            <h2 className="font-display text-[26px] leading-tight text-ink" data-id="element-1380" data-mp="zkav7e6qx2r3">
              What your contribution funds
            </h2>
            <ul className="mt-5 space-y-4" data-id="element-1381" data-mp="1n8u49vxmdjk8">
              {[['₦2,000', 'Prints 40 policy explainer cards for a ward canvassing team.'], ['₦10,000', 'Fuels a mobilisation vehicle for a full day in a rural LGA.'], ['₦50,000', 'Funds a two-hour volunteer training session for 60 people.']].map(([value, detail]) => <li key={value} className="flex gap-4 border-b border-sand-300 pb-4" data-id="element-1382" data-mp="2b4p16qlg4rsg">
                  <span className="w-24 shrink-0 font-display text-[18px] text-green-700" data-id="element-1383" data-mp="266ds4i2z6lba">
                    {value}
                  </span>
                  <span className="text-[15px] leading-relaxed text-slate-600" data-id="element-1384" data-mp="1ihcqpesk8afi">{detail}</span>
                </li>)}
            </ul>

            <div className="mt-8 rounded-media border border-sand-200 bg-white p-6" data-id="element-1385" data-mp="1sfs8us9m09cv">
              <h3 className="flex items-center gap-2 font-display text-[19px] text-ink" data-id="element-1386" data-mp="1ngfl85awyy98">
                <ShieldCheckIcon aria-hidden className="h-5 w-5 text-green-600" data-id="element-1387" data-mp="33ufjlbu42fud" />
                Contribution rules
              </h3>
              <ul className="mt-4 space-y-2.5" data-id="element-1388" data-mp="2yw4c37raghfb">
                {rules.map(rule => <li key={rule} className="flex gap-2.5 text-[14px] leading-relaxed text-slate-600" data-id="element-1389" data-mp="18eqzzju3mnrx">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" data-id="element-1390" data-mp="1dcioe3vjbrrd" />
                    {rule}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7" data-id="element-1391" data-mp="1mervskvib50j">
            <div className="rounded-media border border-sand-200 bg-white p-6 sm:p-9" data-id="element-1392" data-mp="1mbnohemqvt5n">
              <ol className="flex flex-wrap gap-x-6 gap-y-2 text-[13px]" aria-label="Contribution steps" data-id="element-1393" data-mp="1raz24erteij0">
                {(['amount', 'details', 'review'] as Step[]).map((item, index) => {
                const order: Step[] = ['amount', 'details', 'review', 'done'];
                const currentIndex = order.indexOf(step);
                const active = order.indexOf(item) === currentIndex;
                const done = order.indexOf(item) < currentIndex;
                return <li key={item} className="flex items-center gap-2" data-id="element-1394" data-mp="2f0l5lef7zjwt" data-mpi={index}>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold ${done ? 'bg-green-600 text-white' : active ? 'bg-ink text-white' : 'bg-sand-200 text-slate-600'}`} data-id="element-1395" data-mp="3gvo57joqsav">
                        {index + 1}
                      </span>
                      <span className={active ? 'font-semibold text-ink' : 'text-slate-500'} data-id="element-1396" data-mp="i5i51doyns47">
                        {item === 'amount' ? 'Choose amount' : item === 'details' ? 'Your details' : 'Review'}
                      </span>
                    </li>;
              })}
              </ol>

              <div className="mt-8 border-t border-sand-200 pt-8" data-id="element-1397" data-mp="110zrt2dat73q">
                {step === 'amount' && <>
                    <fieldset data-id="element-1398" data-mp="1oy4qaw76s5nc">
                      <legend className="font-display text-[24px] text-ink" data-id="element-1399" data-mp="1oxcvhnleyby4">
                        Choose your contribution
                      </legend>
                      <div className="mt-6 flex flex-wrap gap-2.5" data-id="element-1400" data-mp="1f8l69n7qm5qw">
                        {amounts.map(option => {
                      const active = !custom && amount === option;
                      return <button key={option} type="button" aria-pressed={active} onClick={() => {
                        setAmount(option);
                        setCustom('');
                      }} className={`rounded-lg border px-5 py-3 text-[15px] font-semibold transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 text-ink hover:border-slate-500/60'}`} data-id="element-1401" data-mp="1686jskj1eklt">
                              {option}
                            </button>;
                    })}
                      </div>
                    </fieldset>
                    <div className="mt-6" data-id="element-1402" data-mp="yqi4ygwui4um">
                      <label htmlFor="support-custom" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-1403" data-mp="tq3wwyv8uuwl">
                        Or enter another amount
                      </label>
                      <div className="flex items-center rounded-lg border border-sand-300 bg-white focus-within:border-green-600" data-id="element-1404" data-mp="12ph8bx2xhtjy">
                        <span className="pl-4 text-[15px] text-slate-500" data-id="element-1405" data-mp="39h8ad94ylqz9">₦</span>
                        <input id="support-custom" inputMode="numeric" value={custom} placeholder="0" onChange={event => setCustom(event.target.value.replace(/[^0-9]/g, ''))} className="w-full bg-transparent px-2 py-3 text-[15px] text-ink focus:outline-none" data-id="element-1406" data-mp="34i5b65bnuez7" />
                      </div>
                    </div>
                    <Button size="lg" className="mt-8 w-full" onClick={() => setStep('details')} data-id="element-1407" data-mp="20owjxu02vzz7">
                      Continue
                      <ArrowRightIcon aria-hidden className="h-[18px] w-[18px]" data-id="element-1408" data-mp="29myup19m66zo" />
                    </Button>
                  </>}

                {step === 'details' && <form onSubmit={submitDetails} noValidate data-id="element-1409" data-mp="3l2x8tjjnfj7r">
                    <h2 className="font-display text-[24px] text-ink" data-id="element-1410" data-mp="3l2f5k2ykk4qc">Your details</h2>
                    <div className="mt-6 grid gap-5" data-id="element-1411" data-mp="3q06tyn0098ps">
                      <div data-id="element-1412" data-mp="1ee4y2cxx8ovj">
                        <label htmlFor="s-name" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-1413" data-mp="1edd6rebcxdb2">
                          Full name <span className="text-red-600" data-id="element-1414" data-mp="19ftq67r6sofy">*</span>
                        </label>
                        <input id="s-name" autoComplete="name" value={name} onChange={event => setName(event.target.value)} aria-invalid={Boolean(errors.name)} className={inputClass(Boolean(errors.name))} data-id="element-1415" data-mp="14n0l1irt3e3c" />
                        {errors.name && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-1416" data-mp="lrk89gx3gku3">{errors.name}</p>}
                      </div>
                      <div data-id="element-1417" data-mp="2g2p9kekpkl69">
                        <label htmlFor="s-email" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-1418" data-mp="2g2z146lej0e2">
                          Email <span className="text-red-600" data-id="element-1419" data-mp="2b2kpkekld8ba">*</span>
                        </label>
                        <input id="s-email" type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} aria-invalid={Boolean(errors.email)} className={inputClass(Boolean(errors.email))} data-id="element-1420" data-mp="2j6n0no4q9txw" />
                        {errors.email && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-1421" data-mp="1vgiy7k3a008h">{errors.email}</p>}
                      </div>
                      <div data-id="element-1422" data-mp="25d5jg9ziktb1">
                        <label htmlFor="s-state" className="mb-2 block text-[14px] font-medium text-ink" data-id="element-1423" data-mp="25cvo06vp9txy">
                          State of residence <span className="text-red-600" data-id="element-1424" data-mp="1hm01npa0jqy3">*</span>
                        </label>
                        <input id="s-state" value={state} onChange={event => setState(event.target.value)} aria-invalid={Boolean(errors.state)} className={inputClass(Boolean(errors.state))} data-id="element-1425" data-mp="1cnop36sc32c6" />
                        {errors.state && <p className="mt-1.5 text-[13px] text-red-600" data-id="element-1426" data-mp="3jdicunen0t8r">{errors.state}</p>}
                      </div>
                      <label className="flex items-start gap-3" data-id="element-1427" data-mp="av45eeljqm8q">
                        <input type="checkbox" checked={eligible} onChange={event => setEligible(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#056839]" data-id="element-1428" data-mp="fvi2j1lm0brz" />
                        <span className="text-[14px] leading-relaxed text-slate-600" data-id="element-1429" data-mp="2mkwn01lso0ur">
                          I confirm that I am a Nigerian citizen contributing my own funds, within
                          the limits set by the Electoral Act.
                        </span>
                      </label>
                      {errors.eligible && <p className="text-[13px] text-red-600" data-id="element-1430" data-mp="nf9gqfuufx0k">{errors.eligible}</p>}
                    </div>
                    <div className="mt-8 flex items-center justify-between" data-id="element-1431" data-mp="ifogm23xtim9">
                      <Button variant="ghost" onClick={() => setStep('amount')} data-id="element-1432" data-mp="efe22122xjvs">
                        Back
                      </Button>
                      <Button size="lg" type="submit" data-id="element-1433" data-mp="9egibk1f8hs6">
                        Review contribution
                      </Button>
                    </div>
                  </form>}

                {step === 'review' && <>
                    <h2 className="font-display text-[24px] text-ink" data-id="element-1434" data-mp="1yuy4vaju322y">Review and confirm</h2>
                    <dl className="mt-6 divide-y divide-sand-200 border-y border-sand-200" data-id="element-1435" data-mp="1tv39atp45obk">
                      {[['Amount', finalAmount], ['Name', name], ['Email', email], ['State', state], ['Payment method', 'Card or bank transfer (next step)']].map(([label, value]) => <div key={label} className="flex justify-between gap-6 py-3.5" data-id="element-1436" data-mp="2g2p9l5huofo9">
                          <dt className="text-[14px] text-slate-500" data-id="element-1437" data-mp="1jet2cr8aywpp">{label}</dt>
                          <dd className="text-right text-[15px] font-medium text-ink" data-id="element-1438" data-mp="1netfm8jo11v6">{value}</dd>
                        </div>)}
                    </dl>
                    <div className="mt-8 flex items-center justify-between" data-id="element-1439" data-mp="1xdixw2fh4oby">
                      <Button variant="ghost" onClick={() => setStep('details')} data-id="element-1440" data-mp="1xbfy4qoirgu1">
                        Back
                      </Button>
                      <Button size="lg" loading={processing} onClick={() => {
                    setProcessing(true);
                    window.setTimeout(() => {
                      setProcessing(false);
                      setStep('done');
                    }, 1000);
                  }} data-id="element-1441" data-mp="38s68mlzz6utg">
                        {processing ? 'Processing…' : 'Confirm and pay'}
                      </Button>
                    </div>
                  </>}

                {step === 'done' && <div role="status" className="text-center" data-id="element-1442" data-mp="zj84sqdrjgnd">
                    <CheckCircle2Icon aria-hidden className="mx-auto h-10 w-10 text-green-600" data-id="element-1443" data-mp="1dbxvh848l9qt" />
                    <h2 className="mt-5 font-display text-[26px] text-ink" data-id="element-1444" data-mp="199ay6rosqwm8">
                      Thank you, {name.split(' ')[0]}.
                    </h2>
                    <p className="mx-auto mt-3 max-w-md text-[16px] leading-relaxed text-slate-600" data-id="element-1445" data-mp="1n9vhdoymiug9">
                      Your contribution of {finalAmount} has been received. A receipt has been sent
                      to {email}.
                    </p>
                    <p className="mt-6 inline-block rounded-lg bg-sand-100 px-5 py-3 text-[15px]" data-id="element-1446" data-mp="26vnn3ekldjw7">
                      <span className="text-slate-500" data-id="element-1447" data-mp="1rywmx333g45j">Reference</span>{' '}
                      <span className="font-semibold text-ink" data-id="element-1448" data-mp="1wwy41igylted">
                        ASB-C-{String(Math.floor(Math.random() * 900000) + 100000)}
                      </span>
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-1449" data-mp="1nw9pmmnnlr0y">
                      <Link to="/join" className="inline-flex h-12 items-center rounded-lg bg-green-600 px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-emphasis hover:bg-green-700" data-id="element-1450" data-mp="1nycq60mfn2jx">
                        Volunteer as well
                      </Link>
                      <Link to="/resources" className="inline-flex h-12 items-center rounded-lg border border-ink/20 px-6 text-[15px] font-semibold text-ink transition-colors duration-150 ease-emphasis hover:border-ink/40 hover:bg-ink/[0.04]" data-id="element-1451" data-mp="14790axcv7f92">
                        Share the campaign
                      </Link>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Not able to contribute? Give time instead." description="Two hours a week in your ward is worth more to this campaign than most cheques." primary={{
      label: 'Volunteer',
      to: '/join'
    }} secondary={{
      label: 'Read the contribution rules',
      to: '/faq'
    }} data-id="element-1452" data-mp="3przabv3tbu1q" />
    </>;
}